import { alert } from "@pentacode/app/src/elements/alert-dialog";
import { Exclude, State, Storage, Type, hour, minute, randomUUID, throttle, toDateString } from "@pentacode/core";
import { APIError } from "@pentacode/openapi";
import {
    type PentacodeAPIModels as Models,
    PentacodeAPIClient,
    type PentacodeAPIModels,
    getAvailableActions,
} from "@pentacode/rest";
import { html } from "lit";
import { processCreateTimeLogEventsQueue } from "./create-time-log-events-queue";

export type TimeLogConnection = {
    deviceId: string;
    companyId: number;
    authToken: string;
};

class TimeLogState {
    constructor(init: Partial<TimeLogState> = {}) {
        Object.assign(this, init);
    }

    connection: TimeLogConnection | null = null;
    company: PentacodeAPIModels["Company"] | null = null;
    options: Models["TimeLogOption"][] = [];
    eventCue: PentacodeAPIModels["CreateTimeLogEventParams"][] = [];
    device: Models["TimeLogDevice"] | null = null;

    @Type(() => Date)
    lastSync?: Date;

    @Exclude()
    offline?: boolean;

    @Exclude()
    synching = false;
}

export type PerformActionParams = Pick<
    PentacodeAPIModels["CreateTimeLogEventParams"],
    "employeeId" | "timeEntryId" | "action" | "image" | "positionId" | "comment" | "location" | "meals"
>;

export function getTimeWindow(opt: PentacodeAPIModels["TimeLogOption"]["scheduled"][number]) {
    if (!opt.timeEntry.startPlanned && !opt.timeEntry.startFinal && opt.timeEntry.date !== toDateString(new Date())) {
        return [new Date(0), new Date(0)];
    }

    const start = opt.timeEntry.startFinal
        ? new Date(opt.timeEntry.startFinal)
        : opt.timeEntry.startPlanned
          ? new Date(opt.timeEntry.startPlanned!)
          : new Date();

    return [
        new Date(start.getTime() - opt.settings.allowEarlyStart * hour),
        new Date(start.getTime() + opt.settings.lockAfter * hour),
    ];
}

export function isWithinTimeWindow(opt: PentacodeAPIModels["TimeLogOption"]["scheduled"][number], startBuffer = 0) {
    const [start, end] = getTimeWindow(opt);
    const now = Date.now();
    return now >= start.getTime() - startBuffer * hour && now <= end.getTime();
}

export function getCurrentShiftsByStatus(option: PentacodeAPIModels["TimeLogOption"]) {
    option.scheduled.forEach(
        (option) => (option.availableActions = getAvailableActions(option.timeEntry, option.settings))
    );

    const ready = option.scheduled.find(
        (s) => s.timeEntry.status === "scheduled" && s.availableActions.includes("startShift")
    );

    const upcoming = option.scheduled.find(
        (s) =>
            s.timeEntry.status === "scheduled" && !s.availableActions.includes("startShift") && isWithinTimeWindow(s, 3)
    );

    // 1. Check for paused shifts
    const paused = option.scheduled.find((s) => s.timeEntry.status === "paused" && isWithinTimeWindow(s));

    // 2. Check for active shifts
    const ongoing = option.scheduled.find((s) => s.timeEntry.status === "ongoing" && isWithinTimeWindow(s));

    // 3. Check for completed shifts
    const completed = option.scheduled.find((s) => s.timeEntry.status === "completed" && isWithinTimeWindow(s));

    return { ready, upcoming, paused, ongoing, completed };
}

export class TimeLogger {
    state: TimeLogState = new TimeLogState();

    private _resolveLoad: (value?: unknown) => void;

    loaded = new Promise((resolve) => (this._resolveLoad = resolve));

    private _client: PentacodeAPIClient;
    private _storage: Storage;

    private _pollTimeout: number;

    private _subscribers: Array<(state: TimeLogState) => void> = [];

    constructor(client: PentacodeAPIClient, storage: Storage) {
        this._client = client;
        this._storage = storage;
    }

    subscribe(fn: (state: TimeLogState) => void) {
        if (this._subscribers.indexOf(fn) === -1) {
            this._subscribers.push(fn);
        }
    }

    unsubscribe(fn: (state: TimeLogState) => void) {
        const index = this._subscribers.indexOf(fn);
        if (index !== -1) {
            this._subscribers.splice(index, 1);
        }
    }

    publish() {
        for (const fn of this._subscribers) {
            fn(this.state);
        }
    }

    async connect(code: string) {
        const connection = await this._connect({ code });
        this._client.authToken = connection.authToken;
        await this._setState({ connection });
        await this.synchronize();
    }

    async disconnect() {
        this._client.authToken = undefined;
        await this._setState(new TimeLogState());
    }

    async init() {
        await this._loadState();

        if (this.state.connection) {
            await this._connect(this.state.connection);
            this._client.authToken = this.state.connection?.authToken;
            this.poll();
        }

        this._resolveLoad();
    }

    private async _loadState() {
        try {
            this.state = await this._storage.get(TimeLogState, "TimeLogState", "ptc-time-log-state");
        } catch {
            // expected if no stored state exists
        }
        void this._cleanupOldTimeLoggerState();
    }

    private async _setState(vals: Partial<TimeLogState>) {
        Object.assign(this.state, vals);
        await this._saveState();
        this.publish();
    }

    private async _saveState() {
        await this._storage.set("TimeLogState", "ptc-time-log-state", this.state);
    }

    private async _cleanupOldTimeLoggerState() {
        try {
            const oldState = await this._storage.get(State, "State", "state");
            if (oldState) {
                if (oldState.session) {
                    await alert(
                        html`<div class="vertical layout">
                            <div class="text">Die alte Stempeluhr wurde zum 27.11.2024 abgeschaltet.</div>

                            <div class="text">
                                Anweisungen, wie sie die neue Stempeluhr einrichten finden sie
                                <a
                                    href="https://hilfe.pentacode.app/handbuch/zeiterfassung/#stempeluhr-einrichten"
                                    target="_blank"
                                    >im Handbuch</a
                                >.
                            </div>
                        </div>`,
                        {
                            type: "info",
                            title: "Die Alte Stempeluhr wurde abgeschaltet",
                            preventDismiss: true,
                        }
                    );
                }
                await this._storage.delete("TimeLogState", "time_log").catch(() => {});
                await this._storage.delete("State", "state").catch(() => {});
            }
        } catch {
            // expected if no stored state exists
        }
    }

    private async _handleAPIError(e: APIError) {
        console.error(e);
        switch (e.status) {
            case 0:
                await this._setState({ offline: true });
                break;
            case 401:
                await alert("Ihre Sitzung ist Abgelaufen. Bitte verbinden Sie das Gerät erneut!", {
                    type: "warning",
                    title: "Sitzung Abgelaufen",
                });
                await this._disconnect();
                break;
            default:
                await alert(
                    e.message ||
                        "Es ist ein unerwarteter Fehler aufgetreten. Das Pentacode Team wurde informiert und wird das Problem schnellstmöglich beheben. Bitte versuchen Sie es später noch einmal!",
                    {
                        type: "warning",
                        title: "Unerwarteter Fehler",
                    }
                );
        }
    }

    poll = throttle(async (interval: number = minute * 5) => {
        if (!this._client.authToken) {
            return;
        }

        clearTimeout(this._pollTimeout);
        await this.synchronize();

        this._pollTimeout = window.setTimeout(() => this.poll(interval), interval);
    }, 5000);

    private async _disconnect() {
        await this._setState(new TimeLogState());
    }

    get options() {
        return this.state.options;
    }

    get employees() {
        return this.options.map((option) => option.employee);
    }

    get upcomingShifts() {
        const options = this.options;
        return options
            .flatMap(({ employee, scheduled }) =>
                scheduled
                    .filter((opt) => opt.timeEntry.status === "scheduled" && isWithinTimeWindow(opt))
                    .map(({ timeEntry }) => ({ employee, timeEntry }))
            )
            .sort(
                (a, b) => new Date(a.timeEntry.startPlanned!).getTime() - new Date(b.timeEntry.startPlanned!).getTime()
            );
    }

    get activeShifts() {
        const options = this.options;
        return options
            .flatMap(({ employee, scheduled }) =>
                scheduled
                    .filter((opt) => ["paused", "ongoing"].includes(opt.timeEntry.status) && isWithinTimeWindow(opt))
                    .map(({ timeEntry }) => ({ employee, timeEntry }))
            )
            .sort((a, b) => new Date(a.timeEntry.startFinal!).getTime() - new Date(b.timeEntry.startFinal!).getTime());
    }

    private async _connect(params: { code: string } | TimeLogConnection) {
        if ("code" in params) {
            const connection = await this._client.api.connectTimeLogDevice({}, params);
            await this._setState({ connection });
        } else {
            await this._setState({ connection: params });
        }

        this._client.authToken = this.state.connection!.authToken;
        return this.state.connection!;
    }

    getOption(opts: { staffNumber: string } | { pin: string }): Models["TimeLogOption"] | null {
        const option =
            "pin" in opts
                ? this.state.options.find((item) => item.employee.timeLogPin === opts.pin)
                : this.state.options.find((item) => item.employee.staffNumber === opts.staffNumber);

        if (!option) {
            return null;
        }

        return option;
    }

    async performAction(params: PerformActionParams) {
        if (!this.state.connection || !params.employeeId) return;

        const option = this.state.options.find((o) => o.employee.id === params.employeeId);
        if (!option) return;

        if (params.action === "startShift" && !params.timeEntryId) {
            await this._performUnscheduledStartShiftAction(params, this.state.connection.deviceId, option);
        } else if (params.action === "startShift") {
            await this._performStartShiftAction(params, this.state.connection.deviceId, option);
        } else if (params.action === "startBreak") {
            await this._performStartBreakAction(params, this.state.connection.deviceId, option);
        } else if (params.action === "endBreak") {
            await this._performEndBreakAction(params, this.state.connection.deviceId, option);
        } else if (params.action === "endShift") {
            await this._performEndShiftAction(params, this.state.connection.deviceId, option);
        }
    }

    private async _performUnscheduledStartShiftAction(
        params: PerformActionParams,
        deviceId: string,
        employeeOption: Models["TimeLogOption"]
    ) {
        if (!params.employeeId) return;

        const unscheduledItem = employeeOption.unscheduled.find((u) => u.position.id === params.positionId);
        if (!unscheduledItem) return;

        const time = new Date().toISOString();

        const { settings, position } = unscheduledItem;
        // Generate temporary ID
        const timeEntryId = `temp_${randomUUID()}`;
        const event: PentacodeAPIModels["CreateTimeLogEventParams"] = {
            action: "startShift",
            positionId: params.positionId,
            employeeId: params.employeeId,
            timeEntryId,
            deviceId,
            time,
            comment: params.comment,
            image: params.image,
            location: params.location,
            meals: params.meals,
        };
        employeeOption.scheduled.push({
            settings,
            timeEntry: {
                type: "work",
                id: timeEntryId,
                startPlanned: null,
                endPlanned: null,
                startLogged: time,
                endLogged: null,
                startFinal: null,
                endFinal: null,
                currentBreakStart: null,
                date: toDateString(new Date()),
                status: "ongoing",
                position,
                employeeId: params.employeeId,
            },
            availableActions: ["startBreak", "endShift"],
        });

        await this._commitEvent(event);
    }

    private async _performStartShiftAction(
        params: PerformActionParams,
        deviceId: string,
        employeeOption: Models["TimeLogOption"]
    ) {
        if (!params.employeeId || !params.timeEntryId) return;

        const scheduledItem = employeeOption.scheduled.find((s) => s.timeEntry.id === params.timeEntryId);
        if (!scheduledItem) return;

        const { timeEntry } = scheduledItem;

        const event = this._createTimeLogEvent("startShift", deviceId, timeEntry.id, params.employeeId, params);

        // Modify time entry state and future available actions
        timeEntry.status = "ongoing";
        timeEntry.currentBreakStart = null;
        scheduledItem.availableActions = ["startBreak", "endShift"];

        await this._commitEvent(event);
    }

    private async _performStartBreakAction(
        params: PerformActionParams,
        deviceId: string,
        employeeOption: Models["TimeLogOption"]
    ) {
        if (!params.employeeId || !params.timeEntryId) return;

        const scheduledItem = employeeOption.scheduled.find((s) => s.timeEntry.id === params.timeEntryId);
        if (!scheduledItem) return;

        const { timeEntry } = scheduledItem;

        const event = this._createTimeLogEvent("startBreak", deviceId, timeEntry.id, params.employeeId, params);

        // Modify time entry state and future available actions
        timeEntry.status = "paused";
        timeEntry.currentBreakStart = event.time;
        scheduledItem.availableActions = ["endBreak", "endShift"];

        await this._commitEvent(event);
    }

    private async _performEndBreakAction(
        params: PerformActionParams,
        deviceId: string,
        employeeOption: Models["TimeLogOption"]
    ) {
        if (!params.employeeId || !params.timeEntryId) return;

        const scheduledItem = employeeOption.scheduled.find((s) => s.timeEntry.id === params.timeEntryId);
        if (!scheduledItem) return;

        const { timeEntry } = scheduledItem;

        const event = this._createTimeLogEvent("endBreak", deviceId, timeEntry.id, params.employeeId, params);

        // Modify time entry state and future available actions
        timeEntry.status = "ongoing";
        timeEntry.currentBreakStart = null;
        scheduledItem.availableActions = ["startBreak", "endShift"];

        await this._commitEvent(event);
    }

    private async _performEndShiftAction(
        params: PerformActionParams,
        deviceId: string,
        employeeOption: Models["TimeLogOption"]
    ) {
        if (!params.employeeId || !params.timeEntryId) return;

        const scheduledItem = employeeOption.scheduled.find((s) => s.timeEntry.id === params.timeEntryId);
        if (!scheduledItem) return;

        const { timeEntry } = scheduledItem;

        // TODO: if shift is still paused, we need to create another endBreak event before ending the shift

        const event = this._createTimeLogEvent("endShift", deviceId, timeEntry.id, params.employeeId, params);

        // Modify time entry state and future available actions
        timeEntry.status = "completed";
        scheduledItem.availableActions = [];

        await this._commitEvent(event);
    }

    private _createTimeLogEvent(
        action: "startShift" | "endShift" | "startBreak" | "endBreak",
        deviceId: string,
        timeEntryId: string,
        employeeId: number,
        params: Pick<PerformActionParams, "comment" | "image" | "location" | "meals">
    ): PentacodeAPIModels["CreateTimeLogEventParams"] {
        const time = new Date().toISOString();

        return {
            deviceId,
            action,
            timeEntryId,
            employeeId,
            time,
            comment: params.comment,
            image: params.image,
            location: params.location,
            meals: params.meals,
        };
    }

    private async _commitEvent(event: PentacodeAPIModels["CreateTimeLogEventParams"]) {
        this.state.eventCue.push(event);
        await this._saveState();
        await this.synchronize();
    }

    async synchronize() {
        if (this.state.synching) {
            return;
        }

        await this._setState({ synching: true });

        try {
            await processCreateTimeLogEventsQueue(
                this.state.eventCue,
                this._client.api.createTimeLogEvent.bind(this._client.api)
            );
            await this._fetchCompany();
            await this._fetchDevice();
            await this._fetchOptions();
            await this._setState({ offline: false });
        } catch (e) {
            await this._handleAPIError(e);
        }
        await this._setState({ lastSync: new Date(), synching: false });
    }

    private async _fetchCompany() {
        const company = await this._client.api.getCompany({});
        await this._setState({ company });
    }

    private async _fetchDevice() {
        if (!this.state.connection) {
            return;
        }

        const device = await this._client.api.getTimeLogDevice({ id: this.state.connection.deviceId });

        if (device.colorScheme) {
            document.querySelector("html")?.setAttribute("data-color-scheme", device.colorScheme);
        }

        await this._setState({ device });
    }

    private async _fetchOptions() {
        if (!this.state.connection) {
            throw "Not connected!";
        }

        const timeFrom = new Date();
        timeFrom.setTime(timeFrom.getTime() - 24 * 60 * 60 * 1000);

        const timeTo = new Date(timeFrom);
        timeTo.setDate(timeTo.getDate() + 3);

        const options: PentacodeAPIModels["TimeLogOption"][] = [];
        let res:
            | { limit: number; offset: number; total: number; data: PentacodeAPIModels["TimeLogOption"][] }
            | undefined = undefined;

        while (!res || res.total > res.offset + res.data.length) {
            const limit: number = res?.limit || 1000;
            const offset: number = res ? res.offset + res.data.length : 0;
            res = await this._client.api.getTimeLogOptions({
                deviceId: this.state.connection.deviceId,
                from: timeFrom.toISOString(),
                to: timeTo.toISOString(),
                offset,
                limit,
            });

            options.push(...res.data);
        }

        await this._setState({ options });
    }
}
